import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useMemo} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTable from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import convertDataFrameToRunQueryOutput from 'data-frames/util/legacy/convertDataFrameToRunQueryOutput';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import cellToBlobs from 'insights/analytics-visualizations/blobs/cellToBlobs';
import QueryingBlobLinkList from 'insights/analytics-visualizations/blobs/QueryingBlobLinkList';
import {cellToChipItemIds} from 'insights/analytics-visualizations/chip-items/ChipItems';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import cellToString from 'insights/warehouse/cellToString';
import SortableRunQueryOutputTable from 'insights/warehouse/SortableRunQueryOutputTable';
import useRunQueryOutputTableSortState from 'insights/warehouse/useRunQueryOutputTableSortState';
import {ExperimentalShapashTableOptions} from 'machine-learning/handleClickShapashTableRow';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import LoadingChip from 'ui-library/chips/LoadingChip';
import {CellRenderOptions} from 'ui-library/simple-table/SimpleTable';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import UrlAwareText from 'ui-library/UrlAwareText';
import {JSONValue} from 'util/Types';

type TableParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationTableParametersNode'
>;

const CHIP_SIZE = ChipSize.TABLE_CELL;
export interface TableViewerCellContentProps {
  cell: JSONValue;
  cellRenderOptions: CellRenderOptions;
}

export function renderTableViewerCellContent(props: TableViewerCellContentProps): ReactNode {
  const chipItemIds = cellToChipItemIds(props.cell);
  const blobs = cellToBlobs(props.cell);

  const rowHasBeenInView =
    props.cellRenderOptions.hasRowBeenInView === undefined || props.cellRenderOptions.hasRowBeenInView;

  // As a performance optimization for rich content, we render loading chips instead of chips/blobs until
  // the following criterion are satisfied:
  // - For chips, (a) props.isLoadingChips is false, and (b) chips have been in-view
  // - Blobs have been in-view
  const shouldRenderLoadingChip =
    (chipItemIds != null && !rowHasBeenInView) || (blobs != null && !rowHasBeenInView);

  if (shouldRenderLoadingChip) {
    // Cell has not been displayed in the viewport, render placeholder chip
    return <LoadingChip size={CHIP_SIZE} />;
  }

  if (chipItemIds != null) {
    return chipItemIds.length === 1 ? (
      <BenchChip id={chipItemIds[0]} size={CHIP_SIZE} />
    ) : (
      <ChipList
        chipSize={CHIP_SIZE}
        isVertical={false}
        itemIds={chipItemIds}
        maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
      />
    );
  } else if (blobs) {
    return (
      <QueryingBlobLinkList blobIds={blobs.map((blob: BenchlingBlob) => blob.id)} chipSize={CHIP_SIZE} />
    );
  } else {
    return <UrlAwareText className={css(styles.textCellWrapper)} textValue={cellToString(props.cell)} />;
  }
}

const TABLE_MODE = DataFrameTableMode.RICH;

export interface TableViewerProps {
  dataFrame: DataFrame;
  isLegacyInsights: boolean;
  // Not used by the component, but included in the interface since all VisualizationViewers get the parameters
  // in their interface.
  parameters: TableParameters;
  experimentalShapashTableOptions?: ExperimentalShapashTableOptions;
}

function TableViewer({
  dataFrame,
  experimentalShapashTableOptions,
  isLegacyInsights,
}: TableViewerProps): JSX.Element {
  const runQueryOutput = convertDataFrameToRunQueryOutput(dataFrame, false);
  const {actionOrigin, viewStateId} = BlockViewContext.useContext();
  const {sortState, toggleSort} = useRunQueryOutputTableSortState(viewStateId);
  const dataFrameTableSortOptions = useSimpleTableSortState(viewStateId);
  const sortedDataFrame = useMemo(
    () => sortDataFrame(dataFrame, dataFrameTableSortOptions.sortState, TABLE_MODE),
    [dataFrame, dataFrameTableSortOptions.sortState]
  );

  return isLegacyInsights ? (
    <SortableRunQueryOutputTable
      renderCellContent={(cell: JSONValue, options: CellRenderOptions) =>
        renderTableViewerCellContent({
          cell,
          cellRenderOptions: options,
        })
      }
      runQueryOutput={runQueryOutput}
      rawOutputMode={false}
      sortState={sortState}
      toggleSort={toggleSort}
      calculateInView={true}
      experimentalShapashTableOptions={experimentalShapashTableOptions}
    />
  ) : (
    <DataFrameTable
      actionOrigin={actionOrigin}
      dataFrame={sortedDataFrame}
      clientStateId={viewStateId}
      mode={TABLE_MODE}
      renderIsExcludedColumn={false}
      selectionOptions={{allowRowSelection: false}}
      sortOptions={dataFrameTableSortOptions}
    />
  );
}

export default React.memo(TableViewer);

const styles = StyleSheet.create({
  textCellWrapper: {
    display: 'block',
    maxWidth: 1500,
    overflowX: 'hidden',
    // This is not strictly required but is a safeguard to ensure scrollbars are not unintentionally added
    // to each cell if the y content overflows. Having scrollbars rendered on every cell (due to a line
    // height error) has caused visual and performance issues in the past. The bug is fixed by using the style
    // FONT_PROXIMA_13 in the RunQueryOutputTable component.
    overflowY: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
